import React, { useEffect, useState } from "react";
import { AlertTriangle, Building2 } from "lucide-react";
import api from "@/lib/api";
import { Section, cardCls, EmptyState } from "@/components/admin/ui";
import StripeRevenueCard from "@/components/admin/StripeRevenueCard";
import ExpensesCard from "@/components/admin/ExpensesCard";
import BudgetsCard from "@/components/admin/BudgetsCard";
import ReceiptGalleryCard from "@/components/admin/ReceiptGalleryCard";
import ProfitTrendsCard from "@/components/admin/ProfitTrendsCard";

export default function Finance() {
  const [ventures, setVentures] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [byVenture, setByVenture] = useState(null);
  const [receiptKey, setReceiptKey] = useState(0);

  useEffect(() => {
    api.get("/admin/ventures").then(({ data }) => setVentures(data)).catch(() => {});
    api.get("/admin/stripe/by-venture").then(({ data }) => setByVenture(data)).catch(() => {});
  }, []);

  const onExpensesChange = (data) => { setExpenses(data); setReceiptKey((k) => k + 1); };

  const fmt = (n) => "£" + Number(n || 0).toLocaleString("en-GB");
  const totalExp = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const expenseFor = (vid) => expenses.filter((e) => e.venture_id === vid).reduce((s, e) => s + Number(e.amount || 0), 0);
  const liveFor = (vid) => {
    if (!byVenture?.connected) return null;
    const row = byVenture.ventures.find((r) => r.venture_id === vid);
    return row ? row.gross_captured_minor / 100 : 0;
  };

  return (
    <div data-testid="admin-finance">
      <Section title="Finance" subtitle="Live revenue, expenses and profit — with a clear line between real and demo figures.">
        <StripeRevenueCard />

        <div className="my-6"><ProfitTrendsCard /></div>

        <div className="my-6"><ExpensesCard ventures={ventures} onChange={onExpensesChange} /></div>

        <BudgetsCard expenses={expenses} />

        <ReceiptGalleryCard refreshKey={receiptKey} />

        {/* Accounting integration — honest placeholder, no fake connection */}
        <div className={`${cardCls} p-5 mb-6 flex items-start gap-3`}>
          <Building2 size={18} className="text-bd-gold flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-bd-ivory/80">Accounting sync (Xero / QuickBooks)</p>
            <p className="text-xs text-bd-ivory/40 mt-1">
              Not connected. Expenses are tracked in the ledger above. When your Xero or QuickBooks integration is enabled, expenses and profit will sync automatically alongside Stripe revenue. No accounting connection is active yet — nothing here is simulated.
            </p>
          </div>
        </div>

        {/* Sales by venture */}
        <div className={cardCls}>
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <h2 className="font-serif text-xl text-bd-ivory">By venture</h2>
            {byVenture?.connected
              ? <span className="text-[0.6rem] uppercase tracking-wider border border-emerald-400/50 text-emerald-400 px-2 py-0.5">Live income from Stripe</span>
              : <span className="text-[0.6rem] uppercase tracking-wider border border-bd-gold/40 text-bd-gold px-2 py-0.5">Demo figures</span>}
          </div>
          {ventures.length === 0 ? <EmptyState>No ventures.</EmptyState> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead><tr className="text-left text-bd-ivory/40 text-xs uppercase tracking-wider">
                  <th className="px-6 py-3 font-normal">Venture</th>
                  <th className="px-6 py-3 font-normal">{byVenture?.connected ? "Live revenue (Stripe)" : "Revenue (demo)"}</th>
                  <th className="px-6 py-3 font-normal">Expenses</th>
                  <th className="px-6 py-3 font-normal">Profit</th>
                </tr></thead>
                <tbody className="divide-y divide-white/5">
                  {ventures.map((v) => {
                    const live = liveFor(v.id);
                    const rev = live != null ? live : Number(v.revenue || 0);
                    const exp = expenseFor(v.id);
                    return (
                      <tr key={v.id} className="text-bd-ivory/80">
                        <td className="px-6 py-3">{v.name}</td>
                        <td className="px-6 py-3">{fmt(rev)}{live != null && <span className="ml-2 text-[0.6rem] text-emerald-400 uppercase">live</span>}</td>
                        <td className="px-6 py-3">{fmt(exp)}</td>
                        <td className="px-6 py-3 text-bd-gold">{fmt(rev - exp)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="px-6 py-4 border-t border-white/10 flex items-center gap-2 text-xs text-bd-ivory/40">
            <AlertTriangle size={13} /> Total recorded expenses: {fmt(totalExp)}. Costs/revenue marked "demo" are placeholders until Stripe and accounting are connected.
          </div>
        </div>
      </Section>
    </div>
  );
}
